import React from 'react';
import { motion } from 'framer-motion';
import { Mail, Sparkles } from 'lucide-react';
import { useIsMobile } from '../lib/useIsMobile';
export function HeroSection() {
  const isMobile = useIsMobile();
  const bounceTransition = {
    type: 'spring',
    stiffness: 300,
    damping: 10
  };
  return <section className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 text-center overflow-hidden pt-32 md:pt-40 pb-16 bg-gradient-to-br from-blue-100 via-purple-50 to-pink-100" data-id="element-87">
      {/* Animated background blobs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" data-id="element-88">
        <div className="absolute top-20 left-10 w-64 h-64 bg-yellow-200/40 blob animate-float" data-id="element-89" />
        <div className="absolute top-40 right-20 w-96 h-96 bg-purple-200/30 blob" style={{
        animationDelay: '1s',
        animationDuration: '10s'
      }} data-id="element-90" />
        <div className="absolute bottom-20 left-1/4 w-80 h-80 bg-pink-200/30 blob animate-float" style={{
        animationDelay: '2s'
      }} data-id="element-91" />
        <div className="absolute bottom-40 right-1/3 w-72 h-72 bg-green-200/25 blob" style={{
        animationDelay: '1.5s',
        animationDuration: '12s'
      }} data-id="element-92" />
      </div>

      {/* Main Content */}
      <div className="relative z-20 max-w-6xl mx-auto" data-id="element-93">
        <motion.h1 initial={{
        y: 50,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} transition={{
        delay: 0.2,
        ...bounceTransition
      }} className="font-poppins font-black text-6xl md:text-8xl lg:text-9xl tracking-tight mb-8 text-gray-800 leading-none" data-id="element-94">
          <span className="inline-block hover:scale-110 transition-transform cursor-default" data-id="element-95">
            H
          </span>
          <span className="inline-block hover:scale-110 transition-transform cursor-default" data-id="element-96">
            O
          </span>
          <span className="inline-block hover:scale-110 transition-transform cursor-default" data-id="element-97">
            R
          </span>
          <span className="inline-block hover:scale-110 transition-transform cursor-default" data-id="element-98">
            I
          </span>
          <span className="inline-block hover:scale-110 transition-transform cursor-default" data-id="element-99">
            Z
          </span>
          <span className="inline-block hover:scale-110 transition-transform cursor-default" data-id="element-100">
            O
          </span>
          <span className="inline-block hover:scale-110 transition-transform cursor-default" data-id="element-101">
            N
          </span>
          <span className="inline-block text-gradient-playful hover:scale-110 transition-transform cursor-default" data-id="element-102">
            I
          </span>
          <span className="inline-block text-gradient-playful hover:scale-110 transition-transform cursor-default" data-id="element-103">
            M
          </span>
          <span className="inline-block text-gradient-playful hover:scale-110 transition-transform cursor-default" data-id="element-104">
            A
          </span>
        </motion.h1>

        <motion.div initial={{
        scaleX: 0
      }} animate={{
        scaleX: 1
      }} transition={{
        delay: 0.4,
        duration: 0.6
      }} className="h-2 w-32 md:w-48 bg-gradient-to-r from-coral-500 via-yellow-400 to-purple-400 mx-auto mb-8 rounded-full border-2 border-gray-800" data-id="element-105" />

        <motion.h2 initial={{
        y: 30,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} transition={{
        delay: 0.5,
        ...bounceTransition
      }} className="text-3xl md:text-5xl lg:text-6xl font-fredoka font-bold text-gray-800 mb-6 leading-tight" data-id="element-106">
          Where{' '}
          <motion.span className="inline-block px-4 py-2 md:px-6 md:py-3 bg-gradient-to-r from-orange-400 to-coral-500 text-white rounded-3xl border-4 border-gray-800 shadow-[6px_6px_0px_rgba(45,55,72,1)]" animate={{
          rotate: [2, -2, 2],
          y: [0, -8, 0]
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 0.25
        }} data-id="element-107">
            Brands
          </motion.span>{' '}
          Scale &{' '}
          <motion.span className="inline-block px-4 py-2 md:px-6 md:py-3 bg-gradient-to-r from-green-400 to-emerald-400 text-white rounded-3xl border-4 border-gray-800 shadow-[6px_6px_0px_rgba(45,55,72,1)]" animate={{
          rotate: [2, -2, 2],
          y: [0, -8, 0]
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 0.5
        }} data-id="element-108">
            Talents
          </motion.span>{' '}
          Win
        </motion.h2>

        <motion.p initial={{
        y: 30,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} transition={{
        delay: 0.7,
        ...bounceTransition
      }} className="text-xl md:text-2xl text-gray-700 font-bold max-w-3xl mx-auto leading-relaxed px-4 mb-12" data-id="element-109">
          We connect high-performing talents with brands ready to dominate their
          category
        </motion.p>
      </div>

      {/* CTA Button */}
      <motion.div initial={{
      y: 50,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} transition={{
      delay: 0.9,
      ...bounceTransition
    }} className="relative z-20" data-id="element-110">
        <a href="mailto:hello@horizonima.com" className="btn-playful inline-flex items-center gap-3 group" data-id="element-111">
          <Mail className="w-5 h-5 group-hover:rotate-12 transition-transform" data-id="element-112" />
          <span className="text-lg" data-id="element-113">Get In Touch</span>
          <Sparkles className="w-5 h-5 group-hover:scale-125 transition-transform" data-id="element-114" />
        </a>

        <motion.div animate={{
        y: [0, 10, 0]
      }} transition={{
        duration: 2,
        repeat: Infinity
      }} className="mt-12 flex flex-col items-center gap-3" data-id="element-115">
          <span className="text-xs uppercase tracking-wider text-gray-500 font-bold" data-id="element-116">
            Scroll to Explore
          </span>
        </motion.div>
      </motion.div>
    </section>;
}